import api from 'services/api';
import { array, enforce, num, object, runSchema, str } from 'validation';

// Source of truth for the wire shape: nap-backend
// `routes/scheduling_windows.js` GET /scheduling_windows. The full
// response shape is `{ nodes, pagination, config, filtersConfig }`
// — only `nodes` is consumed here, and each node carries far more
// fields than this LOV needs (timezone, schedules, references,
// exclusions, etc). The projection narrows to the identifiers that
// an LOV row actually renders.

export interface SchedulingWindowSummary {
  readonly scheduling_window_id: number;
  readonly name: string;
  readonly zone_name: string;
}

const summarySchema = object<SchedulingWindowSummary>({
  scheduling_window_id: num(),
  name: str(),
  zone_name: str(),
});

interface SchedulingWindowsResponse {
  readonly nodes: readonly SchedulingWindowSummary[];
}

const responseSchema = object<SchedulingWindowsResponse>({
  nodes: array(summarySchema),
});

const LOV_LIMIT = 1000;

/**
 * Fetch all scheduling windows the current user can see. No params:
 * pagination isn't applied here — the LOV needs the complete set so
 * Typeahead's client-side filter has every option to match against.
 * If the universe of scheduling windows ever grows past a UX-bearable
 * dropdown size, this is the call site to migrate to server-side
 * search.
 */
export const getSchedulingWindowsLOV = async (): Promise<readonly SchedulingWindowSummary[]> => {
  const res = await api.get('/scheduling_windows', {
    params: { limit: LOV_LIMIT },
  });
  const parsed = runSchema(responseSchema, res.data, 'getSchedulingWindowsLOV');
  if (!parsed.ok) {
    throw new Error('getSchedulingWindowsLOV: response failed validation');
  }
  return enforce(parsed.value, parsed.warnings, ['nodes']).nodes;
};
